<template>
  <div>
    <header
      class="header"
      :class="{ 'md:pb-12': !$get(packageCheck, 'upgradeRequiredReasons') }"
    >
      <PromoLogo full class="logo" :to="null" />
    </header>

    <Loader v-if="$apollo.loading" class="bg-white py-6" />

    <div class="bg-white">
      <UpgradeReasons
        v-if="showUpgradeReasons"
        :upgrade-required-reasons="upgradeRequiredReasons"
        :can-download="canDownload"
      />

      <main class="main">
        <section class="w-full" :class="{ 'lg:w-gr-large': freeTier }">
          <UpgradeOffer
            v-if="
              !$apollo.loading && packageCheck && packageCheck.upgradeRequired
            "
            :account-id="$route.query.a"
            :package="$get(packageCheck, 'requiredPackage')"
            :upgrade-required-reasons="upgradeRequiredReasons"
            :free-tier="freeTier"
          />
        </section>

        <section
          v-if="
            !$apollo.loading &&
              freeTier &&
              packageCheck &&
              packageCheck.upgradeRequired
          "
          class="download-wrap"
        >
          <div class="divider-dashed-vertical" />

          <DownloadCard
            :account-id="$route.query.a"
            :campaign-id="$route.query.c"
            :can-download="canDownload"
          />
        </section>
      </main>
    </div>

    <FAQs class="bg-white pb-6 md:pb-10" />

    <UpgradeFooter />
  </div>
</template>

<script>
import includes from "lodash.includes";
import { SendCampaignCheck } from "@/graphql/queries";
import { isAuthError } from "@/lib/auth";
import Loader from "@/components/UI/Loader";
import UpgradeOffer from "@/components/Upgrade/UpgradeOffer";
import UpgradeFooter from "@/components/Upgrade/UpgradeFooter";
import UpgradeReasons from "@/components/Upgrade/UpgradeReasons";
import DownloadCard from "@/components/Upgrade/DownloadCard";
import FAQs from "@/components/FAQs/FAQs";

export default {
  apollo: {
    sendCampaignCheck: {
      query: () => SendCampaignCheck,
      manual: true,
      result: function({ data, loading }) {
        if (!loading) {
          const packageCheck = data?.sendCampaignCheck?.packageCheck;
          const details = data?.sendCampaignCheck?.details;

          // If no upgrade is required then send the user to the plans page...
          if (
            (packageCheck && !packageCheck.upgradeRequired) ||
            !packageCheck
          ) {
            return this.$router.push({
              name: "Plans",
            });
          }

          // Otherwise update the component with the result data...
          this.packageCheck = packageCheck;
          this.details = details;
        }
      },
      variables: function() {
        return {
          input: { campaignId: this.$route.query.c },
        };
      },
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          this.errors = graphQLErrors.map(({ message }) => message);
        }
      },
    },
  },
  name: "Upgrade",
  components: {
    DownloadCard,
    FAQs,
    Loader,
    UpgradeFooter,
    UpgradeOffer,
    UpgradeReasons,
  },
  data() {
    return {
      details: () => {},
      packageCheck: () => {},
    };
  },
  computed: {
    canDownload() {
      const upgradeRequiredReasons =
        this.packageCheck?.upgradeRequiredReasons ?? [];

      // Only let the user download if the upgradeRequiredReasons doesn't include CUSTOM_ELEMENTS_USED and PROMO_BRANDING_REMOVED...
      return (
        !includes(upgradeRequiredReasons, "CUSTOM_ELEMENTS_USED") &&
        !includes(upgradeRequiredReasons, "PROMO_BRANDING_REMOVED")
      );
    },
    freeTier() {
      return this.packageCheck?.currentPackage?.id === "free";
    },
    showUpgradeReasons() {
      return (
        !this.$apollo.loading &&
        this.packageCheck &&
        this.upgradeRequiredReasons.length
      );
    },
    upgradeRequiredReasons() {
      const upgradeRequiredReasons =
        this?.packageCheck?.upgradeRequiredReasons ?? [];

      return upgradeRequiredReasons.filter((reason) => reason !== "FREE_PLAN");
    },
  },
  created() {
    const campaignId = this.$route.query.c;
    const accountId = this.$route.query.a;

    // If somebody tries to hit the upgrade route without having the correct props...
    if (!campaignId || !accountId) {
      this.$router.push({
        name: "Campaigns",
        query: {
          upgradeRouteFail: true,
        },
      });
    }
  },
};
</script>

<style scoped>
.header {
  @apply bg-white;
  padding: 34px 0;
}

.main {
  @apply bg-white flex flex-wrap max-w-2xl mx-auto;

  @screen lg {
    @apply pb-10;
  }
}

.logo {
  @apply block mx-auto;
  max-width: 150px;

  @screen lg {
    @apply mb-2 mt-1;
    max-width: 160px;
  }
}

.divider-dashed-vertical {
  @apply hidden;
  height: 420px;

  @screen lg {
    @apply block;
  }

  @screen xl {
    @apply mr-5;
  }

  @media (min-width: 1400px) {
    @apply mr-10;
  }
}

.download-wrap {
  @apply w-full;

  @screen lg {
    @apply w-gr-small flex;
    margin-top: 233px;
  }
}
</style>

<style>
body {
  @apply bg-grey-lightest;
}
</style>
